import { useEffect, useState } from 'react'

import { POSTHOG_FEATURE_FLAGS, type PostHogFeatureFlag } from '../usePostHogFeatureFlag/posthogFeatureFlags'
import { usePostHogFeatureFlag } from '../usePostHogFeatureFlag/usePostHogFeatureFlag'
import { useFeatureFlagsReady } from '../usePostHogFeatureFlag/useFeatureFlagsReady'
import { useFeatureFlagsReadyWithTimeout } from '../usePostHogFeatureFlag/useFeatureFlagsReadyWithTimeout'
import { BACKEND_POSTHOG_FEATURE_FLAGS } from '../useBackendFeatureFlag/backendPosthogFeatureFlags'
import { useBackendFeatureFlagVariant } from '../useBackendFeatureFlag/useBackendFeatureFlag'
import {
  isGofundmeUserJourneysVariant,
  type GofundmeUserJourneysVariant
} from '@percent/cause-dashboard/constants/gofundmeUserJourneys'

export type GfmBannerElement = 'modal' | 'home_banner' | 'carousel' | 'product_tile'

export const BACKEND_FLAGS_TIMEOUT_MS = 3000

const ELEMENT_FLAGS: Record<GfmBannerElement, PostHogFeatureFlag> = {
  modal: POSTHOG_FEATURE_FLAGS.CP_GFM_MODAL,
  home_banner: POSTHOG_FEATURE_FLAGS.CP_GFM_HOME_BANNER,
  carousel: POSTHOG_FEATURE_FLAGS.CP_GFM_CAROUSEL_BANNER,
  product_tile: POSTHOG_FEATURE_FLAGS.CP_GFM_PRODUCT_TILE
}

export type GfmBannerVariantState = {
  isReady: boolean
  variant: GofundmeUserJourneysVariant | null
}

const useHasTimedOut = (isWaiting: boolean, timeoutMs: number) => {
  const [hasTimedOut, setHasTimedOut] = useState(false)

  useEffect(() => {
    if (!isWaiting) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => setHasTimedOut(true), timeoutMs)
    return () => window.clearTimeout(timeoutId)
  }, [isWaiting, timeoutMs])

  return hasTimedOut
}

export function useGfmBannerVariant(element: GfmBannerElement): GfmBannerVariantState {
  const arePortalFlagsLoaded = useFeatureFlagsReady()
  const arePortalFlagsReady = useFeatureFlagsReadyWithTimeout()
  const isElementEnabled = usePostHogFeatureFlag(ELEMENT_FLAGS[element])
  const testVariant = useBackendFeatureFlagVariant(BACKEND_POSTHOG_FEATURE_FLAGS.GOFUNDME_USER_JOURNEYS_AB_TEST)
  const hasBackendTimedOut = useHasTimedOut(!testVariant.ready, BACKEND_FLAGS_TIMEOUT_MS)

  const [snapshot, setSnapshot] = useState<GfmBannerVariantState>({ isReady: false, variant: null })

  const isElementOff = arePortalFlagsLoaded && !isElementEnabled
  const canDecide = arePortalFlagsReady && (isElementOff || testVariant.ready || hasBackendTimedOut)

  if (!snapshot.isReady && canDecide) {
    const isInTest = arePortalFlagsLoaded && isElementEnabled && testVariant.ready
    const variant = isInTest && isGofundmeUserJourneysVariant(testVariant.value) ? testVariant.value : null
    setSnapshot({ isReady: true, variant })
  }

  return snapshot
}
